import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Save as SaveIcon,
  Message as MessageIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
// import { useAuth } from '../context/AuthContext';
import { getAllCustomers } from '../services/customerService';
import { Customer } from '../types/customer';
// تعريف مجموعات العملاء
enum CustomerGroup {
  ALL = 'all',
  MAINTENANCE = 'maintenance',
  POS = 'pos'
}

// دوال بديلة لخدمة واتساب
const sendPromotionalMessage = (message: string, customers: any[], useSenderPhone: boolean) => {
  console.log('Sending promotional message:', message, customers, useSenderPhone);
  return { success: customers.length, failed: 0 };
};

const setDefaultSenderPhone = (phone: string) => {
  localStorage.setItem('default_sender_phone', phone);
};

const getDefaultSenderPhone = () => {
  return localStorage.getItem('default_sender_phone');
};

const WhatsAppMessagingPage: React.FC = () => {
  // Estado para la configuración de WhatsApp
  const [config, setConfig] = useState({
    enabled: true,
    apiKey: '',
    phoneNumberId: '',
    businessAccountId: '',
    accessToken: '',
    senderPhone: getDefaultSenderPhone() || '',
    useSenderPhone: !!getDefaultSenderPhone()
  });

  // Estado para los mensajes promocionales
  const [message, setMessage] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<CustomerGroup>(CustomerGroup.ALL);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [previewMode, setPreviewMode] = useState(false);

  // Estado para el diálogo de confirmación
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Estado para el progreso de envío
  const [sending, setSending] = useState(false);

  // Estado para las notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'info' | 'warning' | 'error'
  });

  // Estado para el historial de mensajes
  const [messageHistory, setMessageHistory] = useState<any[]>([]);

  // Estado para el modo de edición
  const [editMode, setEditMode] = useState(false);

  // Obtener el contexto de autenticación (no se usa actualmente)
  // const { currentUser } = useAuth();

  // Filtrar clientes por grupo
  const filterCustomersByGroup = React.useCallback(() => {
    if (selectedGroup === CustomerGroup.ALL) {
      setFilteredCustomers(customers);
    } else if (selectedGroup === CustomerGroup.MAINTENANCE) {
      // Filtrar clientes que tienen registros de mantenimiento
      // Esto es una simulación, en un entorno real se consultaría la base de datos
      setFilteredCustomers(customers.filter(customer =>
        customer.devices && customer.devices.length > 0
      ));
    } else if (selectedGroup === CustomerGroup.POS) {
      // Filtrar clientes que han realizado compras en el punto de venta
      // Esto es una simulación, en un entorno real se consultaría la base de datos
      setFilteredCustomers(customers);
    }
  }, [selectedGroup, customers]);

  // Cargar la configuración de WhatsApp y los clientes al montar el componente
  useEffect(() => {
    loadWhatsAppConfig();
    loadCustomers();
  }, []);

  // Filtrar clientes cuando cambia el grupo seleccionado
  useEffect(() => {
    filterCustomersByGroup();
  }, [filterCustomersByGroup]);

  // Cargar la configuración de WhatsApp (simplificado)
  const loadWhatsAppConfig = () => {
    // No es necesario cargar configuración en esta versión simplificada
    // La configuración está siempre habilitada
  };

  // Cargar los clientes
  const loadCustomers = async () => {
    try {
      const allCustomers = await getAllCustomers();
      setCustomers(allCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات العملاء',
        severity: 'error'
      });
    }
  };



  // Manejar cambios en la configuración
  const handleConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    if (name === 'enabled') {
      setConfig(prev => ({ ...prev, [name]: checked }));
    } else {
      setConfig(prev => ({ ...prev, [name]: value }));
    }
  };

  // Guardar la configuración de WhatsApp
  const handleSaveConfig = () => {
    try {
      // Guardar el número de teléfono predeterminado si está habilitado
      if (config.useSenderPhone && config.senderPhone) {
        setDefaultSenderPhone(config.senderPhone);
      } else if (!config.useSenderPhone) {
        // Si se deshabilita, eliminar el número predeterminado
        localStorage.removeItem('default_sender_phone');
      }

      // Mostrar notificación de éxito
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات واتساب بنجاح',
        severity: 'success'
      });
      setEditMode(false);
    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات واتساب',
        severity: 'error'
      });
    }
  };

  // Manejar cambios en el mensaje
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  // Manejar cambios en el grupo seleccionado
  const handleGroupChange = (e: SelectChangeEvent<CustomerGroup>) => {
    setSelectedGroup(e.target.value as CustomerGroup);
  };

  // Manejar la selección de clientes
  const handleCustomerSelection = (customerId: string) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Seleccionar todos los clientes
  const handleSelectAllCustomers = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(customer => customer.id));
    }
  };

  // Mostrar vista previa del mensaje
  const handlePreview = () => {
    setPreviewMode(!previewMode);
  };

  // Abrir el diálogo de confirmación
  const handleOpenConfirmDialog = () => {
    if (!message.trim()) {
      setSnackbar({
        open: true,
        message: 'الرجاء كتابة رسالة قبل الإرسال',
        severity: 'warning'
      });
      return;
    }

    if (selectedCustomers.length === 0) {
      setSnackbar({
        open: true,
        message: 'الرجاء اختيار عميل واحد على الأقل',
        severity: 'warning'
      });
      return;
    }

    setConfirmDialogOpen(true);
  };

  // Cerrar el diálogo de confirmación
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  // Enviar mensaje promocional
  const handleSendMessage = () => {
    try {
      setSending(true);

      // Cerrar el diálogo de confirmación
      setConfirmDialogOpen(false);

      // Obtener los clientes seleccionados
      const selectedCustomersList = customers.filter(customer =>
        selectedCustomers.includes(customer.id)
      );

      // Enviar el mensaje
      const result = sendPromotionalMessage(
        message,
        selectedCustomersList,
        config.useSenderPhone // Usar el número de teléfono predeterminado si está habilitado
      );

      // Actualizar el historial de mensajes
      const newHistoryEntry = {
        id: Date.now().toString(),
        message,
        group: selectedGroup,
        sentTo: selectedCustomersList.length,
        success: result.success,
        failed: result.failed,
        date: new Date()
      };

      setMessageHistory(prev => [newHistoryEntry, ...prev]);

      // Mostrar notificación de éxito
      setSnackbar({
        open: true,
        message: `تم إرسال الرسالة بنجاح إلى ${result.success} عميل، فشل الإرسال إلى ${result.failed} عميل`,
        severity: 'success'
      });

      // Limpiar el formulario
      setMessage('');
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Error sending promotional message:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إرسال الرسالة',
        severity: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  // Cerrar la notificación
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Renderizar la vista previa del mensaje
  const renderMessagePreview = (customerName: string) => {
    return message.replace('{customerName}', customerName);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <WhatsAppIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        إدارة رسائل واتساب
      </Typography>

      <Grid container spacing={3}>
        {/* إعدادات واتساب */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                إعدادات واتساب
              </Typography>
              <Button
                variant={editMode ? "contained" : "outlined"}
                color={editMode ? "primary" : "secondary"}
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? "إلغاء التعديل" : "تعديل الإعدادات"}
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.enabled}
                      onChange={handleConfigChange}
                      name="enabled"
                      disabled={!editMode}
                    />
                  }
                  label="تفعيل خدمة واتساب"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="مفتاح API"
                  name="apiKey"
                  value={config.apiKey}
                  onChange={handleConfigChange}
                  disabled={!editMode}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="معرف رقم الهاتف"
                  name="phoneNumberId"
                  value={config.phoneNumberId}
                  onChange={handleConfigChange}
                  disabled={!editMode}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="معرف الحساب التجاري"
                  name="businessAccountId"
                  value={config.businessAccountId}
                  onChange={handleConfigChange}
                  disabled={!editMode}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="رمز الوصول"
                  name="accessToken"
                  value={config.accessToken}
                  onChange={handleConfigChange}
                  disabled={!editMode}
                  variant="outlined"
                  margin="normal"
                  type="password"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  إعدادات رقم الهاتف المخصص للإرسال
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="رقم الهاتف المخصص للإرسال"
                  name="senderPhone"
                  value={config.senderPhone}
                  onChange={handleConfigChange}
                  disabled={!editMode}
                  variant="outlined"
                  margin="normal"
                  helperText="أدخل رقم الهاتف الذي تريد استخدامه لإرسال رسائل واتساب"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.useSenderPhone}
                      onChange={handleConfigChange}
                      name="useSenderPhone"
                      disabled={!editMode || !config.senderPhone}
                    />
                  }
                  label="استخدام رقم الهاتف المخصص للإرسال"
                />
              </Grid>

              {editMode && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    startIcon={<SaveIcon />}
                  >
                    حفظ الإعدادات
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* تاريخ الرسائل */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              تاريخ الرسائل
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {messageHistory.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center">
                لا توجد رسائل سابقة
              </Typography>
            ) : (
              <List>
                {messageHistory.map((historyItem) => (
                  <ListItem key={historyItem.id} divider>
                    <ListItemText
                      primary={historyItem.message}
                      secondary={`تم الإرسال إلى ${historyItem.sentTo} عميل - ${new Date(historyItem.date).toLocaleString()}`}
                    />
                    <Chip
                      label={`نجاح: ${historyItem.success} | فشل: ${historyItem.failed}`}
                      color={historyItem.failed === 0 ? "success" : "warning"}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* إرسال رسائل ترويجية */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              إرسال رسائل ترويجية
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>مجموعة العملاء</InputLabel>
                  <Select
                    value={selectedGroup}
                    onChange={handleGroupChange}
                    label="مجموعة العملاء"
                  >
                    <MenuItem value={CustomerGroup.ALL}>جميع العملاء</MenuItem>
                    <MenuItem value={CustomerGroup.MAINTENANCE}>عملاء الصيانة</MenuItem>
                    <MenuItem value={CustomerGroup.POS}>عملاء نقطة البيع</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="نص الرسالة"
                  value={message}
                  onChange={handleMessageChange}
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={4}
                  helperText="يمكنك استخدام {customerName} لإضافة اسم العميل في الرسالة"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">
                    <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    العملاء ({filteredCustomers.length})
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleSelectAllCustomers}
                  >
                    {selectedCustomers.length === filteredCustomers.length ? "إلغاء تحديد الكل" : "تحديد الكل"}
                  </Button>
                </Box>

                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
                  <List dense>
                    {filteredCustomers.map((customer) => (
                      <ListItem
                        key={customer.id}
                        onClick={() => handleCustomerSelection(customer.id)}
                        sx={{ bgcolor: selectedCustomers.includes(customer.id) ? 'rgba(25, 118, 210, 0.08)' : 'transparent' }}
                        secondaryAction={
                          <IconButton edge="end" size="small">
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={customer.name}
                          secondary={customer.phone || customer.alternatePhone || 'لا يوجد رقم هاتف'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    onClick={handlePreview}
                    startIcon={<MessageIcon />}
                  >
                    {previewMode ? "إخفاء المعاينة" : "معاينة الرسالة"}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenConfirmDialog}
                    startIcon={<SendIcon />}
                    disabled={!config.enabled || sending || message.trim() === '' || selectedCustomers.length === 0}
                  >
                    {sending ? (
                      <>
                        <CircularProgress size={24} color="inherit" />
                        جاري الإرسال...
                      </>
                    ) : (
                      "إرسال الرسالة"
                    )}
                  </Button>
                </Box>
              </Grid>

              {previewMode && selectedCustomers.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    معاينة الرسالة:
                  </Typography>

                  <Card variant="outlined">
                    <CardHeader
                      title="نموذج رسالة واتساب"
                      subheader={`سيتم إرسال هذه الرسالة إلى ${selectedCustomers.length} عميل`}
                    />
                    <CardContent>
                      <Typography variant="body1">
                        {renderMessagePreview(
                          customers.find(c => c.id === selectedCustomers[0])?.name || 'اسم العميل'
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* مربع حوار تأكيد الإرسال */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>تأكيد إرسال الرسالة</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من إرسال الرسالة إلى {selectedCustomers.length} عميل؟
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ملاحظة: سيتم خصم رسوم الإرسال من رصيد واتساب الخاص بك.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleSendMessage} color="primary" variant="contained">
            تأكيد الإرسال
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة الإشعار */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppMessagingPage;
